import React from 'react';

/**
 * Badge — pastille de statut canonique de l'app.
 *
 * Props:
 *   variant   'grey' | 'blue' | 'amber' | 'green' | 'red'   (defaut 'grey')
 *   dot       boolean — affiche le point colore a gauche (defaut true)
 *   as        'span' | 'button' | ...  — element rendu (defaut 'span').
 *             Permet aux pastilles cliquables (menus de statut) de garder
 *             un vrai <button> tout en partageant l'apparence.
 *   className string  — classes additionnelles (positionnement, marges)
 *   children  node    — le libelle, deja traduit par l'appelant
 *
 * Le composant ne fait QUE de l'apparence : il ne connait aucun statut
 * metier. Le mapping statut -> variante vit dans utils/statusVariant.js.
 */
const VARIANTS = {
  grey:  { bg: '#F1F3F5', text: '#4B5563', border: '#E5E7EB', dot: '#9CA3AF' },
  blue:  { bg: '#EAF1FE', text: '#1D4ED8', border: '#CFE0FB', dot: '#3B82F6' },
  amber: { bg: '#FFF4E5', text: '#B45309', border: '#FCE3BE', dot: '#F59E0B' },
  green: { bg: '#E9F9EF', text: '#15803D', border: '#C6EBD3', dot: '#22C55E' },
  red:   { bg: '#FDECEC', text: '#B91C1C', border: '#F6CFCF', dot: '#EF4444' },
};

const Badge = React.forwardRef(
  ({ variant = 'grey', dot = true, as: Tag = 'span', className = '', children, ...rest }, ref) => {
    const v = VARIANTS?.[variant] || VARIANTS?.grey;
    return (
      <Tag
        ref={ref}
        className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full border text-xs font-semibold whitespace-nowrap ${className}`}
        style={{ backgroundColor: v?.bg, color: v?.text, borderColor: v?.border }}
        {...rest}
      >
        {dot && (
          <span
            className="w-1.5 h-1.5 rounded-full flex-shrink-0"
            style={{ backgroundColor: v?.dot }}
          />
        )}
        {children}
      </Tag>
    );
  }
);

Badge.displayName = 'Badge';

export { VARIANTS as BADGE_VARIANTS };
export default Badge;
